import { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from "recharts";
import { CheckCircle2, TrendingUp, CalendarCheck, Receipt } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/date";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const inr = (n) => `₹${Number(Math.round(n || 0)).toLocaleString("en-IN")}`;

const Kpi = ({ icon: Icon, label, value, sub, tone, testid }) => (
  <Card className="p-5 lg:p-6 pt-fade-up" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-bold mt-2 truncate">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </Card>
);

export default function Paid() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/paid").then(({ data }) => setData(data)); }, []);

  if (!data)
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
      </div>
    );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Paid / Settled</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Invoices cleared on sync (balance reached zero), with collection trends over time.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
        <Kpi testid="paid-total" icon={TrendingUp} label="Total Collected"
             value={inr(data.total_settled_inr)} sub="All settled invoices (INR)"
             tone="bg-emerald-500/10 text-emerald-500" />
        <Kpi testid="paid-this-month" icon={CalendarCheck} label="Collected This Month"
             value={inr(data.this_month_inr)} sub="Cleared in the current month"
             tone="bg-primary/10 text-primary" />
        <Kpi testid="paid-count" icon={Receipt} label="Invoices Settled"
             value={data.count.toLocaleString("en-IN")} sub="Total cleared to date"
             tone="bg-indigo-500/10 text-indigo-500" />
      </div>

      {data.trend.length > 0 && (
        <Card className="p-5 lg:p-6" data-testid="paid-trend-chart">
          <h3 className="font-heading text-lg font-medium mb-1">Collections Trend</h3>
          <p className="text-xs text-muted-foreground mb-4">Amount cleared per month (INR)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.trend} margin={{ left: 10, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false}
                     tickFormatter={(v) => (v >= 1e7 ? `${(v / 1e7).toFixed(1)}Cr` : v >= 1e5 ? `${(v / 1e5).toFixed(0)}L` : v)} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                formatter={(v) => inr(v)} />
              <Bar dataKey="amount_inr" radius={[6, 6, 0, 0]} fill="#10B981" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <Card className="overflow-hidden" data-testid="paid-table">
        <div className="p-5 pb-3 border-b border-border/60">
          <h3 className="font-heading text-lg font-medium flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Recently Settled
          </h3>
        </div>
        {data.recent.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No settled invoices yet. As balances clear on the next sync, they'll appear here.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead className="text-xs uppercase tracking-wider">Invoice</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Type</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Settled On</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent.map((r, i) => (
                  <TableRow key={i} className="hover:bg-muted/40" data-testid={`paid-row-${r.invoice_no}`}>
                    <TableCell className="font-mono text-xs">{r.invoice_no}</TableCell>
                    <TableCell className="font-medium min-w-[160px] max-w-[260px] break-words">{r.customer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={r.source === "domestic"
                        ? "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20"
                        : "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20"}>
                        {r.source}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {fmtDate(r.paid_at)}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">{r.amount_display}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
